import type { Block } from "./state"
import { assertNever, faceFillColor, faceRoleKeys, getSchema, effectiveOrientation, type Face, type BlockSchema, cutSize, cutLength } from "./blockschema"
import { inFraction } from "./plan_utils"
import { DEFAULT_COLORWAY } from "./defaults"

// Re-exported for the many call sites that import the "block default" name.
// The values live in defaults.ts so the quilt-render path (layout.ts) and
// the block-render path here can't drift apart.
export const DEFAULT_BLOCK_COLORS = DEFAULT_COLORWAY

export function hexToValueHex(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  const s = L <= 0.0031308 ? 12.92 * L : 1.055 * L ** (1 / 2.4) - 0.055
  const v = Math.round(Math.max(0, Math.min(255, s * 255)))
  const h = v.toString(16).padStart(2, "0")
  return `#${h}${h}${h}`
}

export function toValueColors(colors: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(colors).map(([k, c]) => [k, hexToValueHex(c)]))
}

export function renderBlock(
  block: Block,
  scale = 40.0,
  colors?: Record<string, string>
): string {
  const c = { ...DEFAULT_BLOCK_COLORS, ...colors }
  const schema = getSchema(block.style)
  const u = block.unit_inches * scale
  const [cols, rows] = schema.grid_units
  const w = cols * u
  const h = rows * u
  const sw = Math.max(1.0, scale * 0.04)

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${f(w)}" height="${f(h)}" viewBox="0 0 ${f(w)} ${f(h)}">`,
    `<rect width="${f(w)}" height="${f(h)}" fill="${c.background}"/>`,
  ]

  for (const face of schema.faces) {
    parts.push(renderFaceSvg(face, schema, block, u, c, sw))
  }

  parts.push("</svg>")
  return parts.join("\n")
}

// Labelled-piece diagram: the block, with each face annotated with patch
// label and cut size. Stroke and labels are stronger than the plain block
// preview so the diagram reads as a reference, not decoration.
export function renderBlockDiagram(
  block: Block,
  scale = 60.0,
  colors?: Record<string, string>
): string {
  const c = { ...DEFAULT_BLOCK_COLORS, ...colors }
  const schema = getSchema(block.style)
  const u = block.unit_inches * scale
  const [cols, rows] = schema.grid_units
  // Reserve a margin on the right for labels that overflow.
  const blockW = cols * u
  const blockH = rows * u
  const margin = Math.max(40, scale * 0.6)
  const w = blockW + margin * 2
  const h = blockH + margin * 2
  const sw = Math.max(1.2, scale * 0.05)
  const ox = margin
  const oy = margin

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${f(w)}" height="${f(h)}" viewBox="0 0 ${f(w)} ${f(h)}">`,
    `<g transform="translate(${f(ox)},${f(oy)})">`,
  ]

  for (const face of schema.faces) {
    parts.push(renderFaceSvg(face, schema, block, u, c, sw))
  }

  // Seam-line overlay: dashed inset rect on square/strip faces showing where
  // the needle runs (¼" inside each cut edge = the finished-size boundary).
  const seamPx = block.seam * scale
  for (const face of schema.faces) {
    if (face.kind !== "square" && face.kind !== "strip") continue
    const fx = face.pos[0] * u + seamPx
    const fy = face.pos[1] * u + seamPx
    const fw = face.size[0] * u - 2 * seamPx
    const fh = face.size[1] * u - 2 * seamPx
    if (fw <= 0 || fh <= 0) continue
    parts.push(
      `<rect x="${f(fx)}" y="${f(fy)}" width="${f(fw)}" height="${f(fh)}" fill="none" stroke="${c.stroke}" stroke-width="0.8" stroke-dasharray="3,2" opacity="0.4"/>`,
    )
  }

  // Grain-direction indicators: ↕ straight grain (square, V-strip), ↔ horizontal
  // (H-strip), 45° bias (hst/qst/flying_goose, shown in red as a handling caution).
  for (const face of schema.faces) {
    const g = grainIndicator(face, u)
    if (g) parts.push(g)
  }

  // Labels: one per patch type, anchored to the centroid of the first face
  // that consumes that patch. Avoids overlap on the goose pair (body + sky)
  // by stacking them in a small block.
  const labelsByPatch = collectFaceLabels(schema)
  const labelFontSize = Math.max(9, Math.round(scale * 0.18))
  for (const { patchName, anchor } of labelsByPatch) {
    const patch = schema.patches.find((p) => p.name === patchName)
    if (!patch) continue
    const cut = cutSize(patch, block)
    // Strip patches show width × length; squares (and triangle squares) show
    // a single edge with "sq" since they're cut from squares.
    const sizeStr = patch.kind === "strip"
      ? `${inFraction(cut)} × ${inFraction(cutLength(patch, block))}`
      : `${inFraction(cut)} sq`
    const text = `${patch.label} · ${sizeStr} · ${patch.count}`
    const [ax, ay] = anchor
    parts.push(
      `<g transform="translate(${f(ax * u)},${f(ay * u)})">`,
      `<circle cx="0" cy="0" r="${f(Math.max(2, scale * 0.04))}" fill="${c.stroke}"/>`,
      `<text x="${f(scale * 0.1)}" y="${f(scale * 0.05)}" font-size="${labelFontSize}" font-family="Helvetica,sans-serif" fill="${c.stroke}">${escapeXml(text)}</text>`,
      `</g>`,
    )
  }

  // Pressing arrows: one per non-open seam edge with a computable boundary.
  for (const edge of schema.seam_graph) {
    if (edge.press === "open") continue
    const fa = schema.faces[edge.a], fb = schema.faces[edge.b]
    if (!fa || !fb) continue
    const b = sharedBoundary(fa, fb)
    if (!b) continue
    const pressedTo = edge.press === "to_a" ? fa : fb
    parts.push(renderPressArrow(b, pressedTo, u))
  }

  parts.push("</g>")
  parts.push("</svg>")
  return parts.join("\n")
}

interface FaceLabel {
  patchName: string
  anchor: [number, number]  // in grid units
}

// One label per patch type; anchor at the centroid of the first face that
// references it (or its body half, for flying geese).
function collectFaceLabels(schema: BlockSchema): FaceLabel[] {
  const seen = new Set<string>()
  const out: FaceLabel[] = []
  for (const face of schema.faces) {
    for (const k of faceRoleKeys(face)) {
      const patchName = face.roles[k]
      if (!patchName || seen.has(patchName)) continue
      seen.add(patchName)
      const [px, py] = face.pos
      const [pw, ph] = face.size
      // For body, anchor at the apex side; for sky, anchor at one of the
      // sky corners. Plain squares anchor at the geometric center; HSTs
      // and QSTs anchor inside one of the triangles their role fills.
      let anchor: [number, number] = [px + pw / 2, py + ph / 2]
      if (face.kind === "flying_goose") {
        anchor = gooseAnchor(face, k)
      } else if (face.kind === "hst") {
        anchor = hstAnchor(face, k)
      } else if (face.kind === "qst") {
        anchor = qstAnchor(face, k)
      }
      out.push({ patchName, anchor })
    }
  }
  return out
}

function hstAnchor(face: Face, roleKey: string): [number, number] {
  const [px, py] = face.pos
  const [pw, ph] = face.size
  const dir = effectiveOrientation(face) ?? "NE"
  // Drop the anchor near the centroid of whichever triangle the role fills.
  // `a` lives in the corner named by `direction`; `b` lives in the opposite.
  const corners: Record<string, [number, number]> = {
    NW: [px + pw * 0.3, py + ph * 0.3],
    NE: [px + pw * 0.7, py + ph * 0.3],
    SE: [px + pw * 0.7, py + ph * 0.7],
    SW: [px + pw * 0.3, py + ph * 0.7],
  }
  const opposite: Record<string, string> = { NE: "SW", SW: "NE", NW: "SE", SE: "NW" }
  return roleKey === "a" ? corners[dir] : corners[opposite[dir]]
}

function qstAnchor(face: Face, roleKey: string): [number, number] {
  const [px, py] = face.pos
  const [pw, ph] = face.size
  const dir = effectiveOrientation(face) ?? "+"
  // QSTs split into 4 triangles meeting at the face center. With "+" the
  // a-pair is N+S; with "x" the a-pair is the TL→BR diagonal pair (the
  // two triangles whose hypotenuse runs along that diagonal: NE+SW).
  // Anchor inside the centroid of the N (or NE) triangle for `a`, S (or
  // SW) for `b` so labels land cleanly in opposite halves.
  if (dir === "+") {
    if (roleKey === "a") return [px + pw / 2, py + ph * 0.18]   // top
    return [px + pw / 2, py + ph * 0.82]                         // bottom
  }
  // "x"
  if (roleKey === "a") return [px + pw * 0.82, py + ph * 0.18]   // NE
  return [px + pw * 0.18, py + ph * 0.82]                         // SW
}

function gooseAnchor(face: Face, roleKey: string): [number, number] {
  const [px, py] = face.pos
  const [pw, ph] = face.size
  const dir = effectiveOrientation(face) ?? "S"
  // Body anchor: midpoint of the apex edge. Sky anchor: midpoint of the
  // far-corner sky triangle (offset by a quarter of the long side).
  if (roleKey === "body") {
    if (dir === "S") return [px + pw / 2, py + ph * 0.66]
    if (dir === "N") return [px + pw / 2, py + ph * 0.33]
    if (dir === "E") return [px + pw * 0.66, py + ph / 2]
    return [px + pw * 0.33, py + ph / 2]
  }
  // sky
  if (dir === "S") return [px + pw * 0.15, py + ph * 0.25]
  if (dir === "N") return [px + pw * 0.15, py + ph * 0.75]
  if (dir === "E") return [px + pw * 0.25, py + ph * 0.15]
  return [px + pw * 0.75, py + ph * 0.15]
}

interface SharedBoundary {
  axis: "H" | "V"
  coord: number   // shared x (V) or y (H) in grid units
  from: number    // span start
  to: number      // span end
}

// Returns the axis-aligned shared boundary between two faces, or null if they
// are not rectilinearly adjacent. Used to position pressing arrows.
export function sharedBoundary(fa: Face, fb: Face): SharedBoundary | null {
  const [ax, ay] = fa.pos, [aw, ah] = fa.size
  const [bx, by] = fb.pos, [bw, bh] = fb.size
  const faR = ax + aw, faB = ay + ah, fbR = bx + bw, fbB = by + bh

  if (faR === bx || fbR === ax) {
    const xCoord = faR === bx ? faR : fbR
    const ov0 = Math.max(ay, by), ov1 = Math.min(faB, fbB)
    if (ov1 > ov0) return { axis: "V", coord: xCoord, from: ov0, to: ov1 }
  }
  if (faB === by || fbB === ay) {
    const yCoord = faB === by ? faB : fbB
    const ov0 = Math.max(ax, bx), ov1 = Math.min(faR, fbR)
    if (ov1 > ov0) return { axis: "H", coord: yCoord, from: ov0, to: ov1 }
  }
  return null
}

function renderPressArrow(b: SharedBoundary, pressedTo: Face, u: number): string {
  const mid = (b.from + b.to) / 2
  const sz = 0.15
  let pts: [number, number][]
  if (b.axis === "V") {
    const [mx, my] = [b.coord, mid]
    pts = pressedTo.pos[0] < b.coord
      ? [[mx - sz, my], [mx + sz * 0.5, my - sz * 0.6], [mx + sz * 0.5, my + sz * 0.6]]
      : [[mx + sz, my], [mx - sz * 0.5, my - sz * 0.6], [mx - sz * 0.5, my + sz * 0.6]]
  } else {
    const [mx, my] = [mid, b.coord]
    pts = pressedTo.pos[1] < b.coord
      ? [[mx, my - sz], [mx - sz * 0.6, my + sz * 0.5], [mx + sz * 0.6, my + sz * 0.5]]
      : [[mx, my + sz], [mx - sz * 0.6, my - sz * 0.5], [mx + sz * 0.6, my - sz * 0.5]]
  }
  const ps = pts.map(([px, py]) => `${f(px * u)},${f(py * u)}`).join(" ")
  return `<polygon points="${ps}" fill="#444" opacity="0.7"/>`
}

function renderFaceSvg(
  face: Face, schema: BlockSchema, block: Block, u: number,
  colors: Record<string, string>, sw: number,
): string {
  const x = face.pos[0] * u
  const y = face.pos[1] * u
  const w = face.size[0] * u
  const h = face.size[1] * u
  const stroke = colors.stroke

  switch (face.kind) {
    case "square":
    case "strip": {
      const fill = faceFillColor(face, "fill", schema, block, colors)
      return rect(x, y, w, h, fill, stroke, sw)
    }
    case "hst": {
      const aColor = faceFillColor(face, "a", schema, block, colors)
      const bColor = faceFillColor(face, "b", schema, block, colors)
      return hst(x, y, w, h, effectiveOrientation(face) ?? "NE", aColor, bColor, stroke, sw)
    }
    case "qst": {
      const aColor = faceFillColor(face, "a", schema, block, colors)
      const bColor = faceFillColor(face, "b", schema, block, colors)
      return qst(x, y, w, h, effectiveOrientation(face) ?? "+", aColor, bColor, stroke, sw)
    }
    case "flying_goose": {
      const bodyColor = faceFillColor(face, "body", schema, block, colors)
      const skyColor = faceFillColor(face, "sky", schema, block, colors)
      return goose(x, y, w, h, effectiveOrientation(face) ?? "S", bodyColor, skyColor, stroke, sw)
    }
    default:
      return assertNever(face.kind)
  }
}

function rect(x: number, y: number, w: number, h: number, fill: string, stroke: string, sw: number): string {
  return `<rect x="${f(x)}" y="${f(y)}" width="${f(w)}" height="${f(h)}" fill="${fill}" stroke="${stroke}" stroke-width="${f(sw)}"/>`
}

function poly(points: [number, number][], fill: string, stroke: string, sw: number): string {
  const pts = points.map(([px, py]) => `${f(px)},${f(py)}`).join(" ")
  return `<polygon points="${pts}" fill="${fill}" stroke="${stroke}" stroke-width="${f(sw)}"/>`
}

function goose(
  x: number, y: number, w: number, h: number, direction: string,
  pointColor: string, skyColor: string, stroke: string, sw: number
): string {
  const tl: [number, number] = [x, y]
  const tr: [number, number] = [x + w, y]
  const bl: [number, number] = [x, y + h]
  const br: [number, number] = [x + w, y + h]
  let skyA: [number, number][], skyB: [number, number][], point: [number, number][]

  if (direction === "S") {
    const apex: [number, number] = [x + w / 2, y + h]
    skyA = [tl, bl, apex]; skyB = [tr, br, apex]; point = [tl, tr, apex]
  } else if (direction === "N") {
    const apex: [number, number] = [x + w / 2, y]
    skyA = [bl, tl, apex]; skyB = [br, tr, apex]; point = [bl, br, apex]
  } else if (direction === "E") {
    const apex: [number, number] = [x + w, y + h / 2]
    skyA = [tl, tr, apex]; skyB = [bl, br, apex]; point = [tl, bl, apex]
  } else {
    const apex: [number, number] = [x, y + h / 2]
    skyA = [tr, tl, apex]; skyB = [br, bl, apex]; point = [tr, br, apex]
  }

  return [
    poly(skyA, skyColor, stroke, sw),
    poly(skyB, skyColor, stroke, sw),
    poly(point, pointColor, stroke, sw),
  ].join("\n")
}

// Half-square triangle: a unit cell split along one diagonal into two
// triangles. `direction` names the corner where the `a` triangle lives;
// the `b` triangle fills the opposite corner. NE/SW share a TL→BR
// diagonal; NW/SE share a TR→BL diagonal.
function hst(
  x: number, y: number, w: number, h: number, direction: string,
  aColor: string, bColor: string, stroke: string, sw: number
): string {
  const tl: [number, number] = [x, y]
  const tr: [number, number] = [x + w, y]
  const bl: [number, number] = [x, y + h]
  const br: [number, number] = [x + w, y + h]
  let triA: [number, number][], triB: [number, number][]

  if (direction === "NE") {
    triA = [tl, tr, br]; triB = [tl, br, bl]
  } else if (direction === "SW") {
    triA = [tl, br, bl]; triB = [tl, tr, br]
  } else if (direction === "SE") {
    triA = [tr, br, bl]; triB = [tr, bl, tl]
  } else {
    // NW
    triA = [tr, bl, tl]; triB = [tr, br, bl]
  }

  return [
    poly(triA, aColor, stroke, sw),
    poly(triB, bColor, stroke, sw),
  ].join("\n")
}

// Quarter-square triangle: a unit cell split along both diagonals into four
// triangles meeting at the center. With "+" orientation the `a` colour fills
// the top + bottom triangles and `b` fills left + right; with "x" the `a`
// colour fills NE + SW (the triangles bordering the TL→BR diagonal) and `b`
// fills the other pair.
function qst(
  x: number, y: number, w: number, h: number, direction: string,
  aColor: string, bColor: string, stroke: string, sw: number
): string {
  const tl: [number, number] = [x, y]
  const tr: [number, number] = [x + w, y]
  const bl: [number, number] = [x, y + h]
  const br: [number, number] = [x + w, y + h]
  const ctr: [number, number] = [x + w / 2, y + h / 2]

  // Four triangles, named by which edge they touch.
  const top: [number, number][]    = [tl, tr, ctr]
  const right: [number, number][]  = [tr, br, ctr]
  const bottom: [number, number][] = [br, bl, ctr]
  const left: [number, number][]   = [bl, tl, ctr]

  let triA1: [number, number][], triA2: [number, number][]
  let triB1: [number, number][], triB2: [number, number][]
  if (direction === "x") {
    // NE + SW pair shares colour `a`; NW + SE pair shares `b`. The four
    // edge-triangles split the same way: top + right are NE-side; bottom +
    // left are SW-side; the cross axis flips colours along the TR→BL line.
    triA1 = top;    triA2 = right
    triB1 = bottom; triB2 = left
  } else {
    triA1 = top;    triA2 = bottom
    triB1 = left;   triB2 = right
  }

  return [
    poly(triA1, aColor, stroke, sw),
    poly(triA2, aColor, stroke, sw),
    poly(triB1, bColor, stroke, sw),
    poly(triB2, bColor, stroke, sw),
  ].join("\n")
}

// Grain-direction mark per face.
//
// Straight-grain (square/strip): double-headed arrow at the top 12% of the
// face, clear of the label dot at the centroid.
//
// Bias faces (HST/QST/goose): one × per triangle region, positioned by the
// same anchor helpers used for labels, then shifted 40% toward the bounding-
// box corner so the × lands near the triangle apex rather than on the label
// dot or the diagonal seam midpoint.
function grainIndicator(face: Face, u: number): string {
  const [px, py] = face.pos
  const [pw, ph] = face.size
  const hl = Math.min(Math.max(pw, ph) * u * 0.10, 20)
  if (hl < 3) return ""

  const straightGrain = (rotDeg: number): string => {
    const cx = (px + pw / 2) * u
    const cy = (py + 0.12 * ph) * u
    const aw = hl * 0.35
    const al = hl * 0.40
    const shaft = `<line x1="${f(cx)}" y1="${f(cy - hl)}" x2="${f(cx)}" y2="${f(cy + hl)}" stroke="#446" stroke-width="0.9"/>`
    const d1 = `M${f(cx)},${f(cy - hl)} L${f(cx - aw)},${f(cy - hl + al)} L${f(cx + aw)},${f(cy - hl + al)} Z`
    const d2 = `M${f(cx)},${f(cy + hl)} L${f(cx - aw)},${f(cy + hl - al)} L${f(cx + aw)},${f(cy + hl - al)} Z`
    return [
      `<g class="grain-straight" transform="rotate(${rotDeg},${f(cx)},${f(cy)})" opacity="0.45">`,
      shaft,
      `<path d="${d1}" fill="#446"/>`,
      `<path d="${d2}" fill="#446"/>`,
      `</g>`,
    ].join("")
  }

  // Shift each raw anchor 40% toward its nearest bounding-box corner so the
  // × lands near the triangle apex, clear of the label dot at the centroid.
  const biasMarks = (rawAnchors: [number, number][]): string => {
    const d = hl * 0.75
    return rawAnchors.map(([ax, ay]) => {
      const nearX = ax < px + pw / 2 ? px : px + pw
      const nearY = ay < py + ph / 2 ? py : py + ph
      const cx = (ax + (nearX - ax) * 0.4) * u
      const cy = (ay + (nearY - ay) * 0.4) * u
      return [
        `<g class="grain-bias" opacity="0.50">`,
        `<line x1="${f(cx - d)}" y1="${f(cy - d)}" x2="${f(cx + d)}" y2="${f(cy + d)}" stroke="#c33" stroke-width="1.4"/>`,
        `<line x1="${f(cx - d)}" y1="${f(cy + d)}" x2="${f(cx + d)}" y2="${f(cy - d)}" stroke="#c33" stroke-width="1.4"/>`,
        `</g>`,
      ].join("")
    }).join("")
  }

  switch (face.kind) {
    case "square":
      return straightGrain(0)
    case "strip":
      return straightGrain(effectiveOrientation(face) === "H" ? 90 : 0)
    case "hst":
      return biasMarks([hstAnchor(face, "a"), hstAnchor(face, "b")])
    case "qst":
      return biasMarks([qstAnchor(face, "a"), qstAnchor(face, "b")])
    case "flying_goose":
      return biasMarks([gooseAnchor(face, "sky")])
    default:
      return assertNever(face.kind)
  }
}

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
}

function f(v: number): string {
  if (Number.isInteger(v)) return String(v)
  return v.toFixed(3).replace(/\.?0+$/, "")
}
